/**
 * Der Zustand der Monteur-App: wer arbeitet, was er sieht, was noch wartet.
 *
 * **Der Kern ist die Überlagerung.** Der Bestand aus `@hwc/demo-data` ist die
 * Sicht des Servers und unveränderlich; die Outbox aus `../offline/speicher`
 * hält, was der Monteur getan hat, solange es niemand angenommen hat. Was auf
 * dem Bildschirm steht, ist beides übereinander — und `07` §7.9 verlangt, dass
 * der Unterschied **sichtbar** bleibt: synchronisiert, offline verfügbar,
 * wartet auf Upload, Konflikt, Aktion fehlgeschlagen.
 *
 * Eine App, die beides in einen Topf wirft, kann diese fünf Zustände nicht
 * anzeigen, weil sie sie nicht kennt. Deshalb zwei Schichten und eine
 * Funktion, die sie zusammenlegt — statt eines veränderlichen Bestands, in dem
 * am Ende niemand mehr sagen kann, was schon draußen ist.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import type { ReactNode } from "react";

import { formatResourceRef } from "@hwc/contracts";
import type { ResourceRef } from "@hwc/contracts";
import type { Bestand, Einsatz, Person, Servicebericht } from "@hwc/demo-data";
import { setzeDienstadressen,
  einsaetzeHeute,
  erzeugeBestand,
  holeStand,
  kommendeEinsaetze,
  meldeStand,
} from "@hwc/demo-data";
import type { EinsatzZustand, Offlineoperation, Serverstand } from "@hwc/sync";
import { istZustand, uebergangErlaubt } from "@hwc/sync";

import { erzeugeEntwurf } from "../features/report/Berichtsentwurf.js";
import { ladeKonfiguration } from "./konfiguration.js";
import {
  LEERE_AUFLAGE,
  auflageVon,
  baueOperation,
  lade,
  loesche,
  neueKennung,
  sichere,
  stelleZu,
} from "../offline/speicher.js";
import type {
  Einsatzauflage,
  LokalerBeitrag,
  LokalerMaterialposten,
  LokalerZeitposten,
  Lokalstand,
} from "../offline/speicher.js";

export interface MonteurSitzung {
  readonly bestand: Bestand;
  readonly monteur: Person;
  readonly heute: Date;
  readonly lokal: Lokalstand;
  /** Simuliert das Funkloch. `07` §7.9: „Der Benutzer darf weiterarbeiten." */
  readonly online: boolean;

  wechsleMonteur(person: Person): void;
  setzeOnline(wert: boolean): void;

  /** Der Zustand eines Einsatzes **einschließlich** ungesendeter Änderungen. */
  zustandVon(einsatz: Einsatz): EinsatzZustand;
  /** Der Berichtsentwurf zu einem Einsatz, sofern der Abschluss einen erzeugt hat. */
  entwurfZu(einsatz: Einsatz): Servicebericht | undefined;
  auflage(einsatz: Einsatz): Einsatzauflage;
  /** Wartet für diesen Einsatz noch etwas auf Zustellung? */
  wartet(einsatz: Einsatz): number;

  schalte(einsatz: Einsatz, nach: EinsatzZustand): void;
  hakeSchritt(einsatz: Einsatz, schrittRef: string, erledigt: boolean): void;
  buchZeit(einsatz: Einsatz, posten: Omit<LokalerZeitposten, "id">): void;
  buchMaterial(einsatz: Einsatz, posten: Omit<LokalerMaterialposten, "id">): void;
  nimmFoto(einsatz: Einsatz, name: string): void;
  setzeNotiz(einsatz: Einsatz, text: string): void;
  /** Begründet einen offen bleibenden Pflichtschritt — `07` §7.8. */
  setzeAusnahme(einsatz: Einsatz, schrittRef: string, text: string): void;
  /** Einen Beitrag in den Auftragsraum schreiben — `07` §7.10. */
  schreibeChat(einsatz: Einsatz, raum: ResourceRef, text: string): void;
  /** Was in dieser Sitzung geschrieben wurde, je Raum. */
  eigeneBeitraege(raum: ResourceRef): readonly LokalerBeitrag[];

  synchronisiere(): void;
  verwirfBefunde(): void;
}

const Kontext = createContext<MonteurSitzung | undefined>(undefined);

const MONTEUR_SCHLUESSEL = "hwc.monteur.person";

export function MonteurRahmen({ children }: { children: ReactNode }) {
  const [heute] = useState(() => new Date());
  const [bestand] = useState(() => erzeugeBestand(heute));
  const [lokal, setzeLokal] = useState<Lokalstand>(() => lade());
  const [online, setzeOnline] = useState(true);

  /**
   * Was der Austauschdienst über die Einsätze sagt.
   *
   * **Die dritte Schicht, und sie fehlte.** Bisher lagen zwei übereinander:
   * der unveränderliche Bestand als Sicht des Servers und die lokale Auflage
   * als das, was noch niemand angenommen hat. Der Server selbst war eine
   * Annahme — er konnte sich nicht ändern, und deshalb kam eine Rückgabe des
   * Büros beim Monteur nie an.
   *
   * Leer heißt „der Dienst kennt nichts", nicht „nicht erreichbar". Die
   * Unterscheidung steht in `online`: Wer offline ist, arbeitet mit dem, was
   * er hat, und das ist nach `07` §7.9 der Normalfall.
   */
  const [geteilt, setzeGeteilt] = useState<Record<string, EinsatzZustand>>({});

  /**
   * Die Berichtsentwürfe, die der Abschluss erzeugt hat.
   *
   * Im Lokalstand und nicht in einem eigenen `useState`: Sie gehören zu dem,
   * was der Monteur erfasst hat, und müssen ein Neuladen überstehen wie
   * Zeiten und Fotos auch. Neben den Auflagen und nicht darin — ein Bericht
   * ist eine eigene Ressource mit eigenem Zustandsraum (`07` §7.16) und ginge
   * sonst beim Räumen des Einsatzzustands mit verloren.
   */
  const entwuerfe = lokal.entwuerfe as Record<string, Servicebericht>;

  const setzeEntwuerfe = useCallback(
    (aendere: (alt: Record<string, Servicebericht>) => Record<string, Servicebericht>) => {
      setzeLokal((alt) => ({
        ...alt,
        entwuerfe: aendere(alt.entwuerfe as Record<string, Servicebericht>),
      }));
    },
    [],
  );

  const monteure = useMemo(
    () => bestand.personen.filter((p) => p.rollen.includes("technician")),
    [bestand],
  );

  const [monteur, setzeMonteur] = useState<Person>(() => {
    let gemerkt: string | null = null;
    try {
      gemerkt = window.localStorage.getItem(MONTEUR_SCHLUESSEL);
    } catch {
      gemerkt = null;
    }
    // Marek Liszka als Vorgabe: Er hat den laufenden Einsatz von heute — der
    // Bildschirm zeigt beim ersten Öffnen etwas, an dem gerade gearbeitet
    // wird, und nicht eine Liste für morgen.
    const treffer = gemerkt
      ? monteure.find((p) => p.ref.opaqueId === gemerkt)
      : undefined;
    return treffer ?? monteure.find((p) => p.kuerzel === "ML") ?? monteure[0]!;
  });

  // Jede Änderung landet sofort im Speicher. Ein Monteur, der die App
  // wegwischt, hat nicht gespeichert — er hat aufgehört zu tippen, und das ist
  // nicht dasselbe.
  useEffect(() => {
    sichere(lokal);
  }, [lokal]);

  /**
   * Die Laufzeitkonfiguration — ADR-044, „die Monteur-App erbt den Weg".
   *
   * **Einmal beim Start und nicht bei jedem Fensterwechsel.** Der geteilte
   * Stand darunter wird wiederholt geholt, weil er sich ändert, während man
   * arbeitet; die Konfiguration des Kundenstacks ändert sich beim Ausrollen,
   * und dann lädt die App ohnehin neu.
   *
   * Was daraus entsteht, sind die Adressen der Dienste. Kommt nichts — im
   * Funkloch der Normalfall —, bleibt es dabei: kein Sprung ins Quellsystem
   * statt eines Verweises, der ins Leere führt.
   */
  useEffect(() => {
    let abgebrochen = false;
    void ladeKonfiguration().then(({ konfiguration }) => {
      if (abgebrochen) return;
      setzeDienstadressen(konfiguration.domains as Record<string, string>);
    });
    return () => { abgebrochen = true; };
  }, []);

  /**
   * Holt, was das Büro entschieden hat.
   *
   * Beim Start und bei jedem Fensterwechsel — dieselbe Überlegung wie im
   * Portal. **Nicht, während der Monteur offline ist**: Der Schalter im Kopf
   * bildet ein Funkloch nach, und ein Funkloch, aus dem trotzdem Daten
   * kommen, wäre keins.
   */
  useEffect(() => {
    let abgebrochen = false;

    const abholen = () => {
      if (!online) return;
      void holeStand().then((stand) => {
        if (abgebrochen || !stand) return;
        const gelesen: Record<string, EinsatzZustand> = {};
        for (const [ref, wert] of Object.entries(stand.einsatzzustand)) {
          if (istZustand(wert)) gelesen[ref] = wert;
        }
        setzeGeteilt(gelesen);
      });
    };

    abholen();
    window.addEventListener("focus", abholen);
    return () => {
      abgebrochen = true;
      window.removeEventListener("focus", abholen);
    };
  }, [online]);

  const wechsleMonteur = useCallback((person: Person) => {
    setzeMonteur(person);
    try {
      window.localStorage.setItem(MONTEUR_SCHLUESSEL, person.ref.opaqueId);
    } catch {
      // s. `speicher.ts`
    }
  }, []);

  /**
   * Der Zustand eines Einsatzes — drei Schichten, in dieser Reihenfolge.
   *
   * Die lokale Auflage gewinnt, weil sie das Neueste ist, was dieser Monteur
   * weiß. Darunter der geteilte Stand, weil er neuer ist als der Bestand.
   * Ganz unten der Bestand als Ausgangslage.
   *
   * **Die Reihenfolge ist keine Geschmacksfrage.** Andersherum überschriebe
   * eine Rückgabe des Büros die Arbeit, die der Monteur gerade offline
   * erfasst hat — und die ist nach `04` §4.12 genau das, was nicht verloren
   * gehen darf.
   */
  const zustandVon = useCallback(
    (einsatz: Einsatz): EinsatzZustand => {
      const ref = formatResourceRef(einsatz.ref);
      return auflageVon(lokal, ref).zustand ?? geteilt[ref] ?? einsatz.zustand;
    },
    [lokal, geteilt],
  );

  const auflage = useCallback(
    (einsatz: Einsatz) => auflageVon(lokal, formatResourceRef(einsatz.ref)),
    [lokal],
  );

  /**
   * Der Berichtsentwurf zu einem Einsatz.
   *
   * **Solange nichts festgeschrieben ist, wird er gerechnet.** Der erste
   * Entwurf speicherte ihn beim Übergang auf `abschluss` und ließ ihn dort
   * stehen — und zeigte damit unter „Was der Kunde später liest" einen Text,
   * der nicht mehr stimmte: Wer die Tätigkeitsnotiz erst danach tippte (der
   * Normalfall, vorher steht man noch am Schrank), sah „Ohne
   * Tätigkeitsnotiz". Eine Vorschau auf das Abzuschickende, die etwas anderes
   * zeigt als das Abzuschickende, ist schlimmer als keine.
   *
   * Festgeschrieben wird beim **Übermitteln**, und ab da gilt der gespeicherte
   * Stand: `07` §7.16 bindet die Freigabe an eine konkrete Version, und die
   * darf sich nicht mehr bewegen, während das Büro sie prüft.
   */
  const entwurfZu = useCallback(
    (einsatz: Einsatz): Servicebericht | undefined => {
      const schluessel = formatResourceRef(einsatz.ref);
      const festgeschrieben = entwuerfe[schluessel];
      if (festgeschrieben) return festgeschrieben;

      const zustand =
        auflageVon(lokal, schluessel).zustand ?? geteilt[schluessel] ?? einsatz.zustand;
      if (zustand !== "abschluss") return undefined;

      return erzeugeEntwurf({
        bestand,
        einsatz,
        auflage: auflageVon(lokal, schluessel),
        monteur: monteur.ref,
      });
    },
    [entwuerfe, lokal, geteilt, bestand, monteur],
  );

  const wartet = useCallback(
    (einsatz: Einsatz) => {
      const ref = formatResourceRef(einsatz.ref);
      return lokal.warteschlange.filter(
        (op) => op.resourceRef === ref || op.resourceRef.startsWith(`${ref}#`),
      ).length;
    },
    [lokal],
  );

  /** Legt eine Operation an und ändert zugleich die lokale Auflage. */
  const erfasse = useCallback(
    (
      einsatz: Einsatz,
      mutation: Offlineoperation["mutation"],
      vorgang: string,
      nutzlast: unknown,
      aendere: (alt: Einsatzauflage) => Einsatzauflage,
    ) => {
      const ref = formatResourceRef(einsatz.ref);
      setzeLokal((alt) => {
        const vorher = alt.auflagen[ref] ?? LEERE_AUFLAGE;
        const op = baueOperation({
          tenantId: bestand.tenant,
          actorRef: formatResourceRef(monteur.ref),
          resourceRef: ref,
          /**
           * **Die Baseline ist die Version des Standes, auf dem die Änderung
           * beruht — und die bleibt hier bei null.**
           *
           * Vorher stand hier `alt.warteschlange.length` mit der Begründung,
           * zwei Geräte erzeugten so unterschiedliche Baselines. Das stimmt
           * und war trotzdem falsch: Der angenommene Serverstand in
           * `synchronisiere` bleibt bei Version 0, weil ihn niemand hochzählt
           * — sein eigener Kommentar sagt, eine erfundene Zahl würde „die
           * Freitextprüfung mit Konflikten fluten, die es nicht gibt".
           *
           * Genau das geschah. Gemessen am 05.09.2026: Tätigkeitsnotiz und
           * beide Ausnahmebegründungen wurden bei der Zustellung abgewiesen —
           * „Der Text wurde inzwischen geändert (Stand 0, bearbeitet wurde
           * 1)" —, obwohl niemand sonst etwas geändert hatte. Der Monteur
           * schrieb, es ging raus, es kam nie an.
           *
           * Zwei Kommentare, jeder für sich plausibel, zusammen ein Fehler.
           * Aufgefallen ist er beim Durchklicken, nicht beim Lesen.
           *
           * Sobald es einen echten Empfänger gibt, kommt die Version aus der
           * Projektion und beide Seiten zählen dieselbe Zahl hoch.
           */
          baselineVersion: 0,
          mutation,
          nutzlast,
          vorgang,
        });
        /**
         * **Ein Freitext ersetzt seinen Vorgänger in der Warteschlange.**
         *
         * Es gibt nur eine aktuelle Fassung eines Textes. Zehn Zwischenstände
         * zuzustellen kostet zehn Prüfungen für ein Ergebnis, und bei
         * `freitext` prüft `pruefeOperation` gegen die Baseline — jeder
         * Zwischenstand nach dem ersten liefe in einen Konflikt, den niemand
         * hat.
         *
         * Für `additiv` gilt das Gegenteil: Zwei Fotos sind zwei Fotos. Und
         * für `zustand` erst recht — die Kette `anfahrt → vor-ort →
         * abschluss` ist die Reihenfolge, in der der Server sie braucht.
         */
        const bereinigt =
          mutation === "freitext"
            ? alt.warteschlange.filter((w) => w.idempotencyKey !== op.idempotencyKey)
            : alt.warteschlange;

        return {
          ...alt,
          auflagen: { ...alt.auflagen, [ref]: aendere(vorher) },
          warteschlange: [...bereinigt, op],
        };
      });
    },
    [bestand.tenant, monteur],
  );

  const schalte = useCallback(
    (einsatz: Einsatz, nach: EinsatzZustand) => {
      const von = zustandVon(einsatz);
      // Doppelt geprüft, und das ist Absicht: Hier, damit die Oberfläche
      // gar nicht erst einen unmöglichen Knopf anbietet — und in
      // `pruefeOperation` bei der Zustellung, weil sich der Serverstand
      // inzwischen geändert haben kann. Die erste Prüfung ist Höflichkeit,
      // die zweite ist die Regel.
      if (!uebergangErlaubt(von, nach)) return;
      // Die Nutzlast ist der Zustandsname als schlichte Zeichenkette — so
      // liest `zielZustand()` in `packages/sync` sie.
      erfasse(einsatz, "zustand", `zustand:${nach}`, nach, (alt) => ({
        ...alt,
        zustand: nach,
      }));

      const schluessel = formatResourceRef(einsatz.ref);

      // Der Abschluss speichert nichts: `entwurfZu` rechnet den Entwurf,
      // solange er einer ist. So zeigt die Abschlussansicht immer das, was
      // gerade abgeschickt würde — auch wenn danach noch getippt wird.

      /**
       * **Das Übermitteln legt ihn dem Büro vor — auf dem letzten Stand.**
       *
       * `zur_pruefung`, nicht `freigegeben`: „Freigabe und externe
       * Veröffentlichung erfolgen erst nach Büroprüfung." Der Monteur schiebt
       * ihn hinüber, mehr nicht.
       *
       * **Er wird dabei neu aufgebaut, nicht nur umgeschaltet.** Gemessen am
       * 05.09.2026: Wer nach „Arbeit fertig" noch die Tätigkeitsnotiz tippte —
       * der Normalfall, denn vorher steht man noch am Schrank —, schickte
       * einen Bericht mit „Ohne Tätigkeitsnotiz" ab. Ein Entwurf, der bei
       * seiner Entstehung stehenbleibt, ist eine Momentaufnahme und kein
       * Entwurf.
       *
       * Kennung und Version bleiben, damit aus einer Aktualisierung kein
       * zweiter Bericht wird: `07` §7.16 bindet die Freigabe an eine konkrete
       * Version.
       */
      if (nach === "uebermittelt") {
        const frisch = erzeugeEntwurf({
          bestand,
          einsatz,
          auflage: auflageVon(lokal, schluessel),
          monteur: monteur.ref,
        });
        if (frisch) {
          setzeEntwuerfe((alt) => ({
            ...alt,
            [schluessel]: { ...frisch, zustand: "zur_pruefung" },
          }));
        }
      }
    },
    [erfasse, zustandVon, bestand, lokal, monteur],
  );

  const hakeSchritt = useCallback(
    (einsatz: Einsatz, schrittRef: string, erledigt: boolean) => {
      erfasse(
        einsatz,
        /**
         * **`additiv`, und das ist ein Zugeständnis.**
         *
         * `04` §4.12 sagt: „Der Arbeitsschritt fällt unter die Statusregel."
         * Das legt `zustand` nahe. Nur prüft `pruefeOperation` bei dieser
         * Mutation gegen `EINSATZ_ZUSTAND` — den Automaten des **Einsatzes**
         * —, und ein Arbeitsschritt führt keinen davon. Jede so gemeldete
         * Operation wird mit „nennt keinen gültigen Zielzustand" abgelehnt.
         *
         * Das ist eine Lücke im Vertrag, nicht in der Spezifikation: Die drei
         * Mutationstypen kennen keinen binären Ressourcenzustand. Fachlich
         * greift die Regel hier ohnehin trivial — jeder Übergang zwischen
         * offen und erledigt ist gültig, und der Punkt des Abschnitts ist ein
         * anderer: dass zwei Monteure an verschiedenen Schritten **keinen**
         * Konflikt erzeugen. Das ist erfüllt, weil jeder Schritt nach ADR-015
         * eine eigene `resourceRef` trägt, und die Entdopplung läuft über den
         * Idempotenzschlüssel.
         *
         * `PWA-003` muss entscheiden, ob `MUTATION` einen vierten Typ
         * bekommt. Bis dahin steht hier der Typ, der durchgeht — und dieser
         * Kommentar, damit die Entscheidung nicht als getroffen gilt.
         */
        "additiv",
        `schritt:${schrittRef}:${erledigt ? "erledigt" : "offen"}`,
        { schrittRef, erledigt },
        (alt) => ({
          ...alt,
          erledigteSchritte: erledigt
            ? [...new Set([...alt.erledigteSchritte, schrittRef])]
            : alt.erledigteSchritte.filter((r) => r !== schrittRef),
        }),
      );
    },
    [erfasse],
  );

  const buchZeit = useCallback(
    (einsatz: Einsatz, posten: Omit<LokalerZeitposten, "id">) => {
      const id = neueKennung();
      erfasse(einsatz, "additiv", `zeit:${id}`, posten, (alt) => ({
        ...alt,
        zeiten: [...alt.zeiten, { ...posten, id }],
      }));
    },
    [erfasse],
  );

  const buchMaterial = useCallback(
    (einsatz: Einsatz, posten: Omit<LokalerMaterialposten, "id">) => {
      const id = neueKennung();
      erfasse(einsatz, "additiv", `material:${id}`, posten, (alt) => ({
        ...alt,
        material: [...alt.material, { ...posten, id }],
      }));
    },
    [erfasse],
  );

  const nimmFoto = useCallback(
    (einsatz: Einsatz, name: string) => {
      const id = neueKennung();
      const aufgenommen = new Date().toISOString();
      erfasse(einsatz, "additiv", `foto:${id}`, { name, aufgenommen }, (alt) => ({
        ...alt,
        fotos: [...alt.fotos, { id, name, aufgenommen }],
      }));
    },
    [erfasse],
  );

  /**
   * Ein Chatbeitrag geht durch dieselbe Outbox wie alles andere.
   *
   * **Er ist `additiv`**, und das ist die einzige der drei Mutationen, die
   * passt: Ein Beitrag kommt hinzu und ersetzt nichts. Zwei Monteure, die
   * gleichzeitig schreiben, haben keinen Konflikt, sondern zwei Beiträge —
   * genau der Fall, den `04` §4.12 mit „additive Notizen und Fotos können in
   * der Regel zusammengeführt werden" meint.
   *
   * Und er geht in die Warteschlange, nicht direkt raus: `07` §7.10 nennt den
   * Bürochat als Weg für Rückfragen vor Ort, und vor Ort ist regelmäßig kein
   * Netz. Ein Chat, der nur online funktioniert, wäre für den Zweck
   * unbrauchbar.
   */
  const schreibeChat = useCallback(
    (einsatz: Einsatz, raum: ResourceRef, text: string) => {
      const sauber = text.trim();
      if (!sauber) return;
      const id = neueKennung();
      erfasse(einsatz, "additiv", `chat:${id}`, { raum: formatResourceRef(raum), text }, (alt) => ({
        ...alt,
        beitraege: [
          ...(alt.beitraege ?? []),
          { id, raum: formatResourceRef(raum), text: sauber, gesendet: new Date().toISOString() },
        ],
      }));
    },
    [erfasse],
  );

  const eigeneBeitraege = useCallback(
    (raum: ResourceRef) => {
      const gesucht = formatResourceRef(raum);
      return Object.values(lokal.auflagen)
        .flatMap((a) => a.beitraege ?? [])
        .filter((b) => b.raum === gesucht);
    },
    [lokal],
  );

  /**
   * Begründet, warum ein Pflichtschritt offen bleibt — `07` §7.8.
   *
   * **`freitext`, wie die Tätigkeitsnotiz.** Es ist einer: frei formuliert,
   * von einem Menschen geschrieben, und bei gleichzeitiger Änderung braucht es
   * nach `04` §4.12 eine Benutzerentscheidung statt einer Regel. Der
   * Idempotenzschlüssel trägt die Schritt-Id, damit zwei Begründungen zu
   * verschiedenen Schritten einander nicht überschreiben.
   *
   * Ein leerer Text nimmt die Begründung zurück — und damit auch die
   * Möglichkeit, den Einsatz zu übermitteln. Das ist gewollt: Wer die
   * Begründung löscht, hat sie sich anders überlegt.
   */
  const setzeAusnahme = useCallback(
    (einsatz: Einsatz, schrittRef: string, text: string) => {
      erfasse(
        einsatz,
        "freitext",
        `ausnahme:${schrittRef}`,
        { schrittRef, text },
        (alt) => {
          const bisher = { ...(alt.ausnahmen ?? {}) };
          if (text.trim()) bisher[schrittRef] = text.trim();
          else delete bisher[schrittRef];
          return { ...alt, ausnahmen: bisher };
        },
      );
    },
    [erfasse],
  );

  const setzeNotiz = useCallback(
    (einsatz: Einsatz, text: string) => {
      // `freitext` und nicht `additiv`: `04` §4.12 verlangt bei gleichzeitig
      // geänderten Freitexten eine Benutzerentscheidung, und diese Regel
      // greift nur, wenn der Mutationstyp sie auslöst.
      erfasse(einsatz, "freitext", "notiz", { text }, (alt) => ({
        ...alt,
        notiz: text,
      }));
    },
    [erfasse],
  );

  /**
   * Zustellen — nur mit Netz.
   *
   * Der angenommene Serverstand kommt aus dem unveränderlichen Bestand: Das
   * ist genau die Sicht, die der Server hätte, wenn er von den lokalen
   * Änderungen noch nichts weiß. Ein Zustandswechsel von `wartet` auf
   * `uebermittelt` fällt damit auf — er ist nach ADR-045 nicht zulässig, und
   * die Prüfung sagt das, statt ihn durchzuwinken.
   */
  const synchronisiere = useCallback(() => {
    if (!online) return;
    setzeLokal((alt) => {
      const stand = (op: Offlineoperation): Serverstand => {
        const einsatz = bestand.einsaetze.find(
          (e) => formatResourceRef(e.ref) === op.resourceRef,
        );
        return {
          ...(einsatz ? { zustand: einsatz.zustand } : {}),
          version: 0,
          verarbeitet: [],
          // Der Monteur darf, was ihm zugewiesen ist. Ein nicht zugewiesener
          // Einsatz ist der Fall, für den `04` §4.12 das lokale Löschen
          // vorsieht — und die einzige Ablehnung, die Daten entfernt.
          erlaubt:
            einsatz?.monteur !== undefined
            && formatResourceRef(einsatz.monteur) === formatResourceRef(monteur.ref),
        };
      };
      /**
       * Wie sich der angenommene Serverstand fortschreibt.
       *
       * Nur der Zustand wandert, und nur bei einer Zustandsoperation. Die
       * Version bleibt: Ohne echten Empfänger gibt es niemanden, der sie
       * hochzählt, und eine erfundene Zahl würde die Freitextprüfung mit
       * Konflikten fluten, die es nicht gibt.
       */
      const fortschreiben = (vorher: Serverstand, op: Offlineoperation): Serverstand => ({
        ...vorher,
        ...(op.mutation === "zustand" && istZustand(op.payload)
          ? { zustand: op.payload }
          : {}),
        verarbeitet: [...vorher.verarbeitet, op.idempotencyKey],
      });

      const { stand: neu, zugestellt } = stelleZu(alt, stand, fortschreiben);

      /**
       * **Nur Zugestelltes wird gemeldet.** Was in der Warteschlange liegt,
       * hat der Server nicht angenommen — es zu melden hieße, die Outbox
       * abzuschaffen und mit ihr die Unterscheidung, die `07` §7.9 verlangt.
       */
      if (zugestellt.length > 0) {
        const zustaende: Record<string, string> = {};
        for (const op of zugestellt) {
          if (op.mutation === "zustand" && istZustand(op.payload)) {
            zustaende[op.resourceRef] = op.payload;
          }
        }
        const erfassung: Record<string, unknown> = {};
        for (const op of zugestellt) {
          const auflage = alt.auflagen[op.resourceRef];
          if (auflage) erfassung[op.resourceRef] = auflage;
        }
        /**
         * Zu jedem zugestellten Wechsel ein Protokolleintrag.
         *
         * `05` §5.15 stellt jeden Statuswechsel unter Auditpflicht. Wer ihn
         * schreibt, ist im Zielbild der Dienst, der die Operation annimmt —
         * hier gibt es keinen, und die App ist die einzige, die weiß, wer
         * geschaltet hat. Ein Protokoll, das erst mit dem echten Backend
         * entsteht, wäre bis dahin eine Zusage ohne Deckung.
         *
         * Anders als im Portal steht hier **kein** `imAuftragVon`: Der
         * Monteur schaltet in eigenem Namen, sonst hätte die Zustellung ihn
         * abgelehnt (`erlaubt` prüft die Zuweisung).
         */
        const protokoll = zugestellt
          .filter((op) => op.mutation === "zustand" && istZustand(op.payload))
          .map((op, i) => ({
            ref: {
              tenant: bestand.tenant,
              provider: "portal" as const,
              type: "audit-entry",
              opaqueId: `au-monteur-${op.id.slice(0, 8)}-${i}`,
            },
            zeitpunkt: new Date().toISOString(),
            objekt: { ...(bestand.einsaetze.find(
              (e) => formatResourceRef(e.ref) === op.resourceRef,
            )?.ref ?? { tenant: bestand.tenant, provider: "vikunja" as const,
              type: "task", opaqueId: op.resourceRef }) },
            handlung: "Zustand gewechselt",
            akteur: monteur.ref,
            nach: op.payload,
          }));

        // Berichte, die dem Büro vorliegen sollen, gehen mit. Ein Entwurf,
        // den der Monteur noch nicht übermittelt hat, bleibt hier: Er ist
        // seiner, bis er ihn abschickt.
        const berichte: Record<string, unknown> = {};
        for (const [ref, entwurf] of Object.entries(entwuerfe)) {
          if (entwurf.zustand !== "entwurf" && zustaende[ref]) {
            berichte[formatResourceRef(entwurf.ref)] = entwurf;
          }
        }

        void meldeStand({
          einsatzzustand: zustaende,
          erfassung,
          audit: protokoll,
          ...(Object.keys(berichte).length > 0 ? { bericht: berichte } : {}),
        });
      }

      return neu;
    });
  }, [bestand, monteur, online, entwuerfe]);

  const verwirfBefunde = useCallback(() => {
    setzeLokal((alt) => ({ ...alt, befunde: [] }));
  }, []);

  const wert = useMemo<MonteurSitzung>(
    () => ({
      bestand,
      monteur,
      heute,
      lokal,
      online,
      wechsleMonteur,
      setzeOnline,
      zustandVon,
      entwurfZu,
      auflage,
      wartet,
      schalte,
      hakeSchritt,
      buchZeit,
      buchMaterial,
      nimmFoto,
      setzeNotiz,
      setzeAusnahme,
      schreibeChat,
      eigeneBeitraege,
      synchronisiere,
      verwirfBefunde,
    }),
    [
      bestand, monteur, heute, lokal, online, wechsleMonteur, zustandVon, entwurfZu,
      auflage,
      wartet, schalte, hakeSchritt, buchZeit, buchMaterial, nimmFoto, setzeNotiz,
      setzeAusnahme, schreibeChat, eigeneBeitraege,
      synchronisiere, verwirfBefunde,
    ],
  );

  return <Kontext.Provider value={wert}>{children}</Kontext.Provider>;
}

export function useMonteur(): MonteurSitzung {
  const wert = useContext(Kontext);
  if (!wert) throw new Error("useMonteur ohne <MonteurRahmen> aufgerufen");
  return wert;
}

/** Die Einsätze dieses Monteurs für heute. */
export function useHeute(): readonly Einsatz[] {
  const { bestand, heute, monteur } = useMonteur();
  return useMemo(
    () => einsaetzeHeute(bestand, heute, monteur.ref),
    [bestand, heute, monteur],
  );
}

/** Die kommenden Einsätze dieses Monteurs. */
export function useKommend(): readonly Einsatz[] {
  const { bestand, heute, monteur } = useMonteur();
  return useMemo(
    () => kommendeEinsaetze(bestand, heute, monteur.ref),
    [bestand, heute, monteur],
  );
}

/** Alles zurücksetzen — der Weg aus einem verkorksten Probierstand. */
export function zurueckstellen(): void {
  loesche();
  window.location.reload();
}
